/**
 * Modal for å opprette konkurranse
 */

import React from 'react';
import { XCircle } from 'lucide-react';
import { COMPETITION_MODES } from '../constants/config';
import { createCompetition } from '../services/firestore';
import { generateCode } from '../utils/formatters';

export const CreateCompetitionModal = ({ onClose, userId }) => {
  const [selectedMode, setSelectedMode] = React.useState(COMPETITION_MODES.FIRST_TO_GOAL);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const mode = formData.get('mode');

    try {
      console.log('Creating competition with userId:', userId);
      
      const competitionData = {
        title: String(formData.get('title')),
        description: String(formData.get('description')),
        goal: mode === COMPETITION_MODES.LONGEST_DISTANCE ? 0 : Number(formData.get('goal')),
        mode: mode,
        code: generateCode(),
        status: 'active',
        creatorId: userId,
        createdAt: new Date().toISOString(),
        startDate: new Date().toISOString(),
        participants: [userId],
        teams: mode === COMPETITION_MODES.TUG_OF_WAR ? { [userId]: 'A' } : {}
      };

      // Add end date ONLY for LONGEST_DISTANCE mode
      if (mode === COMPETITION_MODES.LONGEST_DISTANCE) {
        competitionData.startDate = formData.get('startDate');
        competitionData.endDate = formData.get('endDate');
      } else {
        // For other modes, no end date (ongoing until goal is reached)
        competitionData.endDate = null;
      }

      await createCompetition(competitionData);

      console.log('Competition created successfully!');
      onClose();
    } catch (error) {
      console.error('Error creating competition:', error);
      alert('Kunne ikke opprette konkurranse: ' + error.message);
    }
  };

  const modes = [
    { 
      value: COMPETITION_MODES.FIRST_TO_GOAL, 
      label: 'Førstemann', 
      description: 'Sett et mål',
      goalLabel: 'Mål (km)',
      goalPlaceholder: '50',
      needsDates: false
    },
    { 
      value: COMPETITION_MODES.LONGEST_DISTANCE, 
      label: 'Lengst mulig', 
      description: 'Sett en periode',
      goalLabel: null,
      goalPlaceholder: null,
      needsDates: true
    },
    { 
      value: COMPETITION_MODES.TUG_OF_WAR, 
      label: 'Tauetrekking', 
      description: 'Lag vs Lag',
      goalLabel: 'Område (km)',
      goalPlaceholder: '20',
      goalOptions: [10, 20, 50, 100],
      needsDates: false
    }
  ];

  const currentMode = modes.find(m => m.value === selectedMode);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-t-[3rem] sm:rounded-[3rem] p-8 shadow-2xl animate-in slide-in-from-bottom duration-300">
        <div className="flex justify-between items-center mb-6 px-1">
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">
            Ny Konkurranse
          </h2>
          <button 
            onClick={onClose}
            className="text-slate-300 hover:text-slate-500 transition-colors"
          >
            <XCircle className="w-8 h-8" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1 px-1">
            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
              Navn
            </label>
            <input
              name="title"
              required
              className="w-full p-4 bg-slate-50 rounded-2xl border-none font-black outline-none shadow-inner"
              placeholder="Eks: Sommerformen"
            />
          </div>

          <div className="space-y-1 px-1">
            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
              Beskrivelse
            </label>
            <input
              name="description"
              className="w-full p-4 bg-slate-50 rounded-2xl border-none font-bold outline-none shadow-inner text-sm"
              placeholder="Valgfri beskrivelse..."
            />
          </div>

          <div className="space-y-1.5 px-1">
            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
              Modus
            </label>
            <div className="grid grid-cols-1 gap-2">
              {modes.map((mode, index) => (
                <label
                  key={mode.value}
                  className={`flex items-center gap-3 p-4 bg-slate-50 rounded-2xl cursor-pointer hover:bg-orange-50 transition-colors border-2 ${
                    selectedMode === mode.value 
                      ? 'border-orange-500/20 bg-orange-50' 
                      : 'border-transparent'
                  } shadow-sm`}
                >
                  <input
                    type="radio"
                    name="mode"
                    value={mode.value}
                    checked={selectedMode === mode.value}
                    onChange={(e) => setSelectedMode(e.target.value)}
                    className="hidden"
                  />
                  <div className="text-left">
                    <p className="font-black text-[10px] uppercase tracking-wider text-slate-700">
                      {mode.label}
                    </p>
                    <p className="text-[9px] font-bold text-slate-400 lowercase">
                      {mode.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {currentMode?.needsDates && (
            <>
              <div className="space-y-1 px-1">
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
                  Startdato
                </label>
                <input
                  name="startDate"
                  type="date"
                  required
                  defaultValue={new Date().toISOString().split('T')[0]}
                  className="w-full p-4 bg-slate-50 rounded-2xl border-none font-black outline-none shadow-inner"
                />
              </div>

              <div className="space-y-1 px-1">
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
                  Sluttdato
                </label>
                <input
                  name="endDate"
                  type="date"
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full p-4 bg-slate-50 rounded-2xl border-none font-black outline-none shadow-inner"
                />
              </div>
            </>
          )}

          {currentMode?.goalLabel && (
            <div className="space-y-1 px-1">
              <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">
                {currentMode.goalLabel}
              </label>
              {currentMode.goalOptions ? (
                <select
                  name="goal"
                  required
                  defaultValue={currentMode.goalPlaceholder}
                  className="w-full p-4 bg-slate-50 rounded-2xl border-none font-black outline-none shadow-inner"
                >
                  {currentMode.goalOptions.map(opt => (
                    <option key={opt} value={opt}>{opt} km</option>
                  ))}
                </select>
              ) : (
                <input
                  name="goal"
                  type="number"
                  required
                  defaultValue={currentMode.goalPlaceholder}
                  className="w-full p-4 bg-slate-50 rounded-2xl border-none font-black outline-none shadow-inner"
                />
              )}
            </div>
          )}

          <button
            type="submit"
            className="w-full py-4 mt-2 bg-orange-600 text-white font-black rounded-2xl shadow-xl shadow-orange-100 hover:bg-orange-700 active:scale-95 transition-all uppercase tracking-[0.15em] text-[11px]"
          >
            Start LØPDA-utfordring
          </button>
        </form>
      </div>
    </div>
  );
};
